import React, { useState, useEffect, useContext, useMemo } from 'react';
import Helmet from "react-helmet";
import { Link, NavLink } from "react-router-dom";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HostApi from "../../api/host/HostApi";
import InstanceApi from "../../api/instance/InstanceApi";

// Types
import HostsDataTable from "../../types/host/tables/HostsDataTable";
import InstanceTargetHost from "../../types/instance/InstanceTargetHost";
import HostTarget from "../../types/host/HostTarget";
import ChartSeries from "../../types/ChartSeries";
import InstanceTarget from "../../types/instance/InstanceTarget";
import HostStatisticV2 from "../../types/host/HostStatisticV2";

// Components
import PeriodBreadcrumb from "../../component/PeriodBreadcrumb";
import HeaderActions from "../../container/header_actions";
import WidgetCard from "../../component/instance/widget";
import Breadcrumb from "../../container/breadcrumb";
import HostsTable from "../../component/host/table/HostsTable";
import ConditionalRender from "../../helpers/ConditionalRender";
import FilterPanel from "../../component/FilterPanel";

// Hooks
import { useHostFilters } from "../../hooks/useHostFilters";

// Helpers
import Helper from "../../helpers/Helper";
import { chartOptionsGranularity } from "../../helpers/chartOptionsIncreasedGranularity";
import { getConvertedTimeToUTC, highchartsCredits } from "../../helpers/utils";

// Constants
import { CHART_DATA_TYPE, DATA_LOADED, DATA_LOADING } from "../../component/Constants";

// Contexts
import { TimeRangeContext } from "../../context/TimeRangeContext";
import { buildApiQuery } from "../../helpers/timeRangeUtils";
import { LicenceContext } from "../../context/LicenceContext";

interface IListProps {
    instances: InstanceTarget[],
    hosts: HostTarget[],
    toggleMenu: Function,
}

const List: React.FC<IListProps> = (props) => {
    const [loading, setLoading] = useState<number>(DATA_LOADING)
    const [chartLoading, setChartLoading] = useState<boolean>(true)
    const [tableDataLoading, setTableDataLoading] = useState<number>(DATA_LOADING)
    const [refreshCounter, setRefreshCounter] = useState<number>(0)

    const [hostsDataTable, setHostsDataTable] = useState<HostsDataTable[]>([])

    const [processorSeries, setProcessorSeries] = useState<ChartSeries[]>([])
    const [ioSeries, setIoSeries] = useState<ChartSeries[]>([])
    const [peakProcessorSeries, setPeakProcessorSeries] = useState<number>(0)
    const [ioUtilisationSeries, setIoUtilisationSeries] = useState<number>(0)

    const [averageProcessorMetric, setAverageProcessorMetric] = useState<number>(0)
    const [previousAverageProcessorMetric, setPreviousAverageProcessorMetric] = useState<number | null>(0)
    const [averageMemoryMetric, setAverageMemoryMetric] = useState<number>(0)
    const [previousAverageMemoryMetric, setPreviousAverageMemoryMetric] = useState<number | null>(0)
    const [averageDiskMetric, setAverageDiskMetric] = useState<number | null>(0)
    const [previousAverageDiskMetric, setPreviousAverageDiskMetric] = useState<number | null>(0)

    const timeRangeContext = useContext(TimeRangeContext)
    const licences = useContext(LicenceContext)

    const { hosts } = props;

    const comparisonSupported = useMemo(() => {
        return Helper.isComparisonSupported(timeRangeContext)
    }, [timeRangeContext]);

    // Use custom hook for filtering
    const {
        filters,
        updateFilter,
        clearAllFilters,
        availableFilterOptions,
        getFilteredHosts
    } = useHostFilters({
        hosts: hosts
    });

    const filteredHosts = useMemo(() => {
        return getFilteredHosts(hosts);
    }, [getFilteredHosts, hosts]);

    const filteredHostsDataTable = useMemo(() => {
        const filteredHostIds = new Set(filteredHosts.map(h => h.id));
        return hostsDataTable.filter(row => filteredHostIds.has(row.id));
    }, [hostsDataTable, filteredHosts]);

    useEffect(() => {
        let active = true;

        async function load() {
            if (hosts.length === 0) {
                setHostsDataTable([]);
                setProcessorSeries([]);
                setIoSeries([]);
                setLoading(DATA_LOADED);
                setTableDataLoading(DATA_LOADED);
                setChartLoading(false);
                return;
            }

            setLoading(DATA_LOADING);
            setTableDataLoading(DATA_LOADING);
            setChartLoading(true);

            const query = buildApiQuery(timeRangeContext.timeRange);
            const prevQuery = buildApiQuery(timeRangeContext.timeRange, true);
            const hostIds = hosts.map(host => Number(host.id));

            const promises: Promise<any>[] = [
                HostApi.getHostStatisticV2Summary(query),           // 0: global summary (current)
                HostApi.getHostStatisticV2ById(query, hostIds),     // 1: per-host summary (current)
                InstanceApi.getInstanceHostMapping(),               // 2: instance ↔ host mapping
            ];

            if (comparisonSupported) {
                promises.push(HostApi.getHostStatisticV2Summary(prevQuery));        // 3: global summary (previous)
                promises.push(HostApi.getHostStatisticV2ById(prevQuery, hostIds));  // 4: per-host summary (previous)
            }

            try {
                const results = await Promise.all(promises);
                if (!active) return;

                const currentSummary: HostStatisticV2[] = results[0];
                const perHostSummary: HostStatisticV2[] = results[1];
                const instanceHostMap: InstanceTargetHost[] = results[2];
                const previousSummary: HostStatisticV2[] = comparisonSupported ? results[3] : [];
                const previousPerHostSummary: HostStatisticV2[] = comparisonSupported ? results[4] : [];

                // Widget averages — pulled from a single global summary row.
                const current = currentSummary[0];
                setAverageProcessorMetric(current ? Math.round(Number(current.cpuutilisation) || 0) : 0);
                setAverageMemoryMetric(current ? Math.round(Number(current.memoryutilisation) || 0) : 0);
                setAverageDiskMetric(current ? Math.round(Number(current.ioutilisation) || 0) : 0);

                if (comparisonSupported) {
                    const previous = previousSummary[0];
                    setPreviousAverageProcessorMetric(previous ? Math.round(Number(previous.cpuutilisation) || 0) : null);
                    setPreviousAverageMemoryMetric(previous ? Math.round(Number(previous.memoryutilisation) || 0) : null);
                    setPreviousAverageDiskMetric(previous ? Math.round(Number(previous.ioutilisation) || 0) : null);
                } else {
                    setPreviousAverageProcessorMetric(null);
                    setPreviousAverageMemoryMetric(null);
                    setPreviousAverageDiskMetric(null);
                }

                // Per-host table data — one row per host from the /id endpoint.
                const statsByHostId: Map<number, HostStatisticV2> = new Map();
                perHostSummary.forEach(row => {
                    if (row.id !== undefined && row.id !== null) {
                        statsByHostId.set(Number(row.id), row);
                    }
                });
                const prevStatsByHostId: Map<number, HostStatisticV2> = new Map();
                previousPerHostSummary.forEach(row => {
                    if (row.id !== undefined && row.id !== null) {
                        prevStatsByHostId.set(Number(row.id), row);
                    }
                });

                const tableData: HostsDataTable[] = hosts.map(host => {
                    const stat = statsByHostId.get(Number(host.id));
                    const prevStat = prevStatsByHostId.get(Number(host.id));
                    const matchedInstanceIds = instanceHostMap.filter(item => Number(item.hostid) === Number(host.id));
                    const matchedInstances: InstanceTarget[] = matchedInstanceIds.flatMap(item =>
                        props.instances.filter(instance => Number(instance.id) === Number(item.id))
                    );

                    return {
                        id: host.id,
                        name: host.name,
                        type: host.type,
                        processor: stat ? Math.round(Number(stat.cpuutilisation) || 0) : 0,
                        // Peak isn't included in the summary row; derived per-host below from the time series.
                        processorPeak: 0,
                        memory: stat ? Math.round(Number(stat.memoryutilisation) || 0) : 0,
                        ioutilisation: stat ? Math.round(Number(stat.ioutilisation) || 0) : 0,
                        previousProcessor: prevStat ? Math.round(Number(prevStat.cpuutilisation) || 0) : null,
                        previousProcessorPeak: null,
                        previousMemory: prevStat ? Math.round(Number(prevStat.memoryutilisation) || 0) : null,
                        previousIoutilisation: prevStat ? Math.round(Number(prevStat.ioutilisation) || 0) : null,
                        instances: matchedInstances,
                    };
                });

                setTableDataLoading(DATA_LOADED);
                setHostsDataTable(tableData);

                // Top hosts over time — one v2 time call per host (each call returns CPU+IO+memory together).
                // When comparison is supported, also fetch the previous-period time series so we
                // can populate the per-host CPU peak comparison without extra round-trips.
                const timeSeriesPromises = hosts.map(host =>
                    HostApi.getHostStatisticV2Time(query, [host.id]).then(data => ({ host, data }))
                );
                const prevTimeSeriesPromises: Promise<{ host: HostTarget, data: HostStatisticV2[] }>[] =
                    comparisonSupported
                        ? hosts.map(host => HostApi.getHostStatisticV2Time(prevQuery, [host.id]).then(data => ({ host, data })))
                        : [];
                const [timeSeriesResults, prevTimeSeriesResults] = await Promise.all([
                    Promise.all(timeSeriesPromises),
                    Promise.all(prevTimeSeriesPromises),
                ]);
                if (!active) return;

                const procSeries: any[] = [];
                const ioSeriesNew: any[] = [];
                const procPeaks: number[] = [];
                const ioPeaks: number[] = [];
                const peakByHostId: Map<number, number> = new Map();

                timeSeriesResults.forEach(({ host, data }) => {
                    const procPoints: number[][] = data.map(item => [
                        getConvertedTimeToUTC(item),
                        Number(item.cpuutilisation) || 0,
                    ]);
                    const ioPoints: number[][] = data.map(item => [
                        getConvertedTimeToUTC(item),
                        Number(item.ioutilisation) || 0,
                    ]);

                    procSeries.push({
                        name: host.name,
                        color: host.color,
                        type: 'spline',
                        minHeight: 3,
                        data: procPoints,
                        zIndex: 1,
                    });

                    ioSeriesNew.push({
                        name: host.name,
                        color: host.color,
                        type: 'spline',
                        minHeight: 3,
                        data: ioPoints,
                        zIndex: 1,
                    });

                    const procValues = procPoints.map(p => p[1]);
                    const ioValues = ioPoints.map(p => p[1]);
                    procPeaks.push(Helper.getPeakValue(procValues));
                    ioPeaks.push(Helper.getPeakValue(ioValues));
                    peakByHostId.set(Number(host.id), Math.round(Helper.getPeakValue(procValues)));
                });

                setProcessorSeries(procSeries);
                setIoSeries(ioSeriesNew);
                setPeakProcessorSeries(Math.round(Helper.getPeakValue(procPeaks)));
                setIoUtilisationSeries(Math.round(Helper.getPeakValue(ioPeaks)));
                setChartLoading(false);

                // Previous-period peaks for the row comparison widget.
                const prevPeakByHostId: Map<number, number> = new Map();
                prevTimeSeriesResults.forEach(({ host, data }) => {
                    const cpuValues = data.map(item => Number(item.cpuutilisation) || 0);
                    prevPeakByHostId.set(Number(host.id), Math.round(Helper.getPeakValue(cpuValues)));
                });

                // Backfill per-host CPU peak now that time series have arrived.
                setHostsDataTable(prev => prev.map(row => ({
                    ...row,
                    processorPeak: peakByHostId.get(Number(row.id)) ?? row.processorPeak,
                    previousProcessorPeak: prevPeakByHostId.get(Number(row.id)) ?? row.previousProcessorPeak ?? null,
                })));

                setLoading(DATA_LOADED);
            } catch (error) {
                console.error('Failed to load host list data.', error);
                if (active) {
                    setLoading(DATA_LOADED);
                    setTableDataLoading(DATA_LOADED);
                    setChartLoading(false);
                }
            }
        }

        void load();
        return () => {
            active = false;
        };
    }, [timeRangeContext, comparisonSupported, hosts, props.instances, refreshCounter]);

    const refresh = () => {
        setLoading(DATA_LOADING)
        setHostsDataTable([])
        setRefreshCounter(c => c + 1)
    }

    const processorChartOptions = chartOptionsGranularity(processorSeries, 100, {
        credits: { enabled: highchartsCredits(licences.licences) },
        legend: true,
        timeRangeContext: timeRangeContext,
        tooltip: {
            formatter: function () {
                return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.PERCENTAGE);
            },
        },
    }, 300)

    const ioChartOptions = chartOptionsGranularity(ioSeries, 100, {
        credits: { enabled: highchartsCredits(licences.licences) },
        legend: true,
        timeRangeContext: timeRangeContext,
        tooltip: {
            formatter: function () {
                return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.PERCENTAGE);
            },
        },
    }, 300)

    return (
        <div id="content">
            <Helmet>
                <title>Hosts - DBmarlin</title>
                <meta name="description" content="List of all monitored hosts with high-level performance statistics"/>
            </Helmet>

            {/* Heading */}
            <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                <Breadcrumb heading="Hosts" type="hosts">
                    <Link to="/hosts">Analysis</Link>
                    <NavLink to="/hosts" className={"current-breadcrumb-link"}>Hosts</NavLink>
                    <PeriodBreadcrumb/>
                </Breadcrumb>
               <HeaderActions toggleMenu={props.toggleMenu} refresh={refresh}
                              />
            </div>

            <div className="loader">
                <ConditionalRender if={loading === DATA_LOADING}>
                    <div className="bar"/>
                </ConditionalRender>
            </div>

            {/* Filter Panel */}
            <div className="row">
                <div className="col">
                    <FilterPanel
                        filterGroups={[
                            {
                                name: 'sensorstatus',
                                label: 'Sensor Status',
                                options: availableFilterOptions.sensorstatuses,
                                selectedValues: filters.sensorstatus || [],
                                onSelectionChange: (values: string[]) => updateFilter('sensorstatus', values)
                            },
                            {
                                name: 'type',
                                label: 'Type',
                                options: availableFilterOptions.types,
                                selectedValues: filters.type,
                                onSelectionChange: (values: string[]) => updateFilter('type', values)
                            },
                            {
                                name: 'agent',
                                label: 'Agent',
                                options: availableFilterOptions.agents,
                                selectedValues: filters.agent || [],
                                onSelectionChange: (values: string[]) => updateFilter('agent', values)
                            }
                        ]}
                        onClearAll={clearAllFilters}
                        isLoading={loading === DATA_LOADING}
                    />
                </div>
            </div>

            {/* Metric Widgets - Host */}
            <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3">
                <WidgetCard
                    name="CPU Average"
                    type='percent'
                    iconClassName="processor"
                    icon="fa-microchip"
                    value={averageProcessorMetric}
                    previous={previousAverageProcessorMetric ?? undefined}
                    cardTooltipText="The average CPU utilization"
                    loading={loading === DATA_LOADING}
                />
                <WidgetCard
                    name="Memory Average"
                    type='percent'
                    iconClassName="memory"
                    icon="fa-memory"
                    value={averageMemoryMetric}
                    previous={previousAverageMemoryMetric ?? undefined}
                    cardTooltipText="The average memory utilization"
                    loading={loading === DATA_LOADING}
                />
                <WidgetCard
                    name="Disk I/O"
                    type='percent'
                    iconClassName="io"
                    icon="fa-hdd"
                    value={averageDiskMetric ?? 0}
                    previous={previousAverageDiskMetric ?? undefined}
                    cardTooltipText="The average disk I/O utilization"
                    loading={loading === DATA_LOADING}
                />
            </div>

            {/* Charts */}
            <div className="row">
                <div className="col">
                    <div className="card collapsible">
                        <div className="card-header">
                            <i className="fal fa-chart-bar fa-fw" aria-hidden="true"/>
                            Top hosts over time
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                               data-bs-target="#collapseCharts" aria-expanded="false" aria-controls="collapseCharts"/>
                        </div>
                        <div id="collapseCharts" className="card-body collapse show p-0">
                            <div className="row row-cols-1 row-cols-lg-2">
                                <div className="col p-3 z-index-10">
                                    <h4>Average CPU<span className="peak">Peak: {peakProcessorSeries}%</span></h4>
                                    <ConditionalRender if={chartLoading}>
                                        <div className="w-100 text-center text-muted mt-3 time-chart">
                                            <div className="loader spinner">
                                            </div>
                                            <p className="mt-3">
                                                Loading data...
                                            </p>
                                        </div>
                                    </ConditionalRender>
                                    <ConditionalRender if={!chartLoading}>
                                        <HighchartsReact constructorType={"chart"} highcharts={Highcharts}
                                                         options={processorChartOptions}/>
                                    </ConditionalRender>
                                </div>
                                <div className="col p-3 border-start z-index-9">
                                    <h4>Disk I/O<span className="peak">Peak: {ioUtilisationSeries}%</span></h4>
                                    <ConditionalRender if={chartLoading}>
                                        <div className="w-100 text-center text-muted mt-3 time-chart">
                                            <div className="loader spinner">
                                            </div>
                                            <p className="mt-3">
                                                Loading data...
                                            </p>
                                        </div>
                                    </ConditionalRender>
                                    <ConditionalRender if={!chartLoading}>
                                        <HighchartsReact constructorType={"chart"} highcharts={Highcharts}
                                                         options={ioChartOptions}/>
                                    </ConditionalRender>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Hosts */}
            <div className="row row-cols-1">
                <div className="col">
                    <div className="card collapsible">
                        <div className="card-header">
                            <i className="fal fa-server fa-fw" aria-hidden="true"/>
                            Monitored hosts
                            <span className="badge bg-info"
                                  data-tooltip-id="global-tooltip" data-tooltip-content="Total host count">{filteredHostsDataTable.length.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</span>
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                               data-bs-target="#collapseInstances" aria-expanded="false"
                               aria-controls="collapseInstances"/>
                        </div>
                        <div id="collapseInstances" className="card-body collapse show">
                            <HostsTable loading={tableDataLoading} hostsDataTable={filteredHostsDataTable}/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default List;
